import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faListAlt, IconDefinition as RegularIconDefinition } from '@fortawesome/free-regular-svg-icons';
import {
  faBars,
  faBell,
  faBook,
  faCalendar,
  faGamepad,
  faGraduationCap,
  faHandshake,
  faHollyBerry,
  faSearch,
  faShoppingCart,
  faUserGraduate,
  faVideo,
  IconDefinition as SolidIconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Container, Dropdown, Nav, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useGaming } from '../../../contexts/GamingContext';
import { useGeolocation } from '../../../contexts/GeolocationContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { canAccessFeature, canEnterEcm, canSeeShop, CloudFunctions, getFunctionsUrl, isItalian } from '../../utils/utils';
import { IconButton } from '../IconButton/IconButton';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { LoginModal } from '../LoginModal/LoginModal';
import { Logo } from '../Logo/Logo';
import { NotificationsList } from '../NotificationsList/NotificationsList';
import { ProfileCircle } from '../ProfileCircle/ProfileCircle';
import './Topbar.scss';

interface ButtonDefinition {
  url?: string;
  externalUrl?: string;
  externalTokenizedUrl?: boolean;
  icon?: SolidIconDefinition | RegularIconDefinition;
  label?: string;
  condition?: () => boolean;
  normalClass?: string;
  activeClass?: string;
  skipDefaultClass?: boolean;
  onClick?: () => void;
  isDropdown?: boolean;
  dropDownOptions?: ButtonDefinition[];
}

const TopbarButton = ({
  buttonDefinition,
  pagePath,
  margin = true,
  isPublicPage = false,
}: {
  buttonDefinition: ButtonDefinition;
  pagePath: string;
  margin?: boolean;
  isPublicPage?: boolean;
}) => {
  const {
    url,
    icon = null,
    label,
    normalClass = '',
    activeClass = '',
    skipDefaultClass,
    externalUrl,
    externalTokenizedUrl,
    isDropdown = false,
    dropDownOptions = [],
  } = buttonDefinition;
  const { currentUser } = useFirebase();
  const history = useHistory();

  if (isDropdown && !dropDownOptions.find((option) => !option.condition || option.condition())) return null;

  return !isPublicPage ? (
    <>
      {isDropdown ? (
        <Dropdown
          className={classNames(normalClass, {
            'topbar-button': !skipDefaultClass,
            'mr-1': margin,
            [activeClass]: pagePath == url,
            'd-inline-block': true,
          })}
          style={{ border: 'none' }}
        >
          <Dropdown.Toggle
            as={Button}
            variant={'outline-light'}
            disabled={pagePath == url}
            active={pagePath == url}
            className={classNames(normalClass, {
              'topbar-button': !skipDefaultClass,
              'mr-1': margin,
              [activeClass]: pagePath == url,
            })}
            style={{ border: 'none', backgroundColor: 'transparent !important' }} // Add backgroundColor override
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              {!!icon && <FontAwesomeIcon icon={icon} className="me-2" />}
              {label && <span className={`d-none d-xl-inline-block`}>{label}</span>}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu align={'right'}>
            {dropDownOptions
              .map((option, i) => {
                return !option.condition || option.condition() ? (
                  <TopbarDropdownNavItem
                    key={i.toString()}
                    pagePath={pagePath}
                    url={option.url}
                    icon={option.icon}
                    label={option.label}
                    onClick={option.onClick}
                    button={option}
                  />
                ) : null;
              })
              .filter(Boolean)}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <Button
          onClick={async () => {
            if (externalUrl) window.open(externalUrl, '_blank');
            else if (externalTokenizedUrl) {
              const url = new URL(getFunctionsUrl(CloudFunctions.GetFadAuthenticatedUrl));
              url.searchParams.append('uid', currentUser.uid);
              url.searchParams.append('token', await currentUser.getIdToken(true));
              url.searchParams.append('email', currentUser.email);
              window.location.href = url.toString();
            } else history.push(url);
          }}
          variant={'outline-light'}
          disabled={pagePath == url}
          active={pagePath == url}
          className={classNames(normalClass, { 'topbar-button': !skipDefaultClass, 'mr-1': margin, [activeClass]: pagePath == url })}
          style={{ border: 'none' }}
        >
          <div style={{ whiteSpace: 'nowrap' }}>
            {!!icon && <FontAwesomeIcon icon={icon} />}
            {label && <span className={`d-none d-xl-inline-block ${icon ? ' ml-0 ml-lg-2' : ''}`}>{label}</span>}
          </div>
        </Button>
      )}
    </>
  ) : (
    <Button
      as={Link}
      to={url}
      variant={'outline-light'}
      disabled={pagePath == url}
      active={pagePath == url}
      className={classNames(normalClass, { 'topbar-button': !skipDefaultClass, 'mr-1': margin, [activeClass]: pagePath == url })}
      style={{ border: 'none' }}
    >
      <div style={{ whiteSpace: 'nowrap' }}>
        {!!icon && <FontAwesomeIcon icon={icon} />}
        {label && <span className={`d-none d-xl-inline-block ${icon ? ' ml-0 ml-lg-2' : ''}`}>{label}</span>}
      </div>
    </Button>
  );
};

const TopbarDropdownNavItem = ({ url, pagePath, icon = null, label, onClick = null, button = null }) => {
  const { currentUser } = useFirebase();
  return !!button && button.externalTokenizedUrl ? (
    <Dropdown.Item
      disabled={pagePath == url}
      onClick={async () => {
        const url = new URL(getFunctionsUrl(CloudFunctions.GetFadAuthenticatedUrl));
        url.searchParams.append('uid', currentUser.uid);
        url.searchParams.append('token', await currentUser.getIdToken(true));
        url.searchParams.append('email', currentUser.email);
        window.location.href = url.toString();
      }}
    >
      <div className={'d-flex justify-content-between align-items-center'}>
        <span className={pagePath == url ? 'text-primary' : ''}>{label}</span>
        {!!icon && <FontAwesomeIcon size={'xs'} icon={icon} className={'ml-2 ' + (pagePath == url ? 'text-primary' : 'text-muted')} />}
      </div>
    </Dropdown.Item>
  ) : (
    <Dropdown.Item as={Link} to={url} disabled={pagePath == url} onClick={onClick}>
      <div className={'d-flex justify-content-between align-items-center'}>
        <span className={pagePath == url ? 'text-primary' : ''}>{label}</span>
        {!!icon && <FontAwesomeIcon size={'xs'} icon={icon} className={'ml-2 ' + (pagePath == url ? 'text-primary' : 'text-muted')} />}
      </div>
    </Dropdown.Item>
  );
};

const TopbarComponent = ({ bg = 'transparent', variant = 'dark', inHeader = false, showLoginButtonIfNeeded = true }) => {
  const { isLoggedIn, notificationBadge, cleanNotifications, subscription, userDataFmt, staticData, userCompanyData } = useFirebase();
  const { isEnabled: isGamingEnabled } = useGaming();
  const { isNative } = useCapacitor();
  const { isMobile } = useMobileDetect();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pagePath, setPagePath] = useState<string>(null);
  const [isAso, setIsAso] = useState(false);
  const [companyId, setCompanyId] = useState<string>(null);
  const [companyName, setCompanyName] = useState<string>(null);
  const [isCompanyEnabled, setIsCompanyEnabled] = useState(false);
  const { t } = useTranslation();
  const { country_code } = useGeolocation();
  const { currentLanguage } = useCurrentLanguage();

  const buttons: ButtonDefinition[] = useMemo(
    () => [
      {
        url: '/browse',
        icon: faVideo,
        label: t('labels.video'),
      },
      {
        url: '/courses',
        icon: faGraduationCap,
        label: t('labels.corsi'),
        condition: () => !!isAso && isItalian(country_code),
      },
      {
        url: '/list',
        icon: faListAlt,
        label: t('labels.mieiToot'),
      },
      {
        url: '/tootors',
        icon: faUserGraduate,
        label: t('labels.tootors'),
        condition: () => !!staticData.SHOW_TOOTORS_LIST,
      },
      {
        url: '/play',
        icon: faGamepad,
        label: 'Gamification',
        condition: () => isGamingEnabled,
      },
      {
        url: '/books',
        icon: faBook,
        label: 'Library',
        condition: () => canAccessFeature('BOOKS', currentLanguage, staticData, userDataFmt),
      },
      {
        url: `/company/${companyId}/courses`,
        icon: faHandshake,
        label: companyName,
        condition: () => !!staticData.ENABLE_COMPANIES && companyId != null && isCompanyEnabled && !isAso,
      },
      {
        url: '/acty',
        icon: faCalendar,
        label: t('acty.labels.title'),
        condition: () => !!staticData.ENABLE_ACTY,
      },
      {
        url: '/live',
        icon: faYoutube,
        label: 'Tootor Live',
        condition: () => canAccessFeature('LIVE', currentLanguage, staticData, userDataFmt),
      },
      {
        url: '/advent',
        icon: faHollyBerry,
        label: t('labels.calendarioAvvento'),
        condition: () => !!staticData.SHOW_ADVENT_CALENDAR_2022 && !isAso && currentLanguage === 'it',
        skipDefaultClass: true,
        normalClass: 'advent-calendar-button',
        activeClass: 'advent-calendar-button-active',
      },
      {
        externalTokenizedUrl: true,
        icon: faGraduationCap,
        label: 'Accedi a ECM',
        condition: () =>
          canAccessFeature('ECM', currentLanguage, staticData, userDataFmt) && canEnterEcm(subscription, userDataFmt) && !isAso && currentLanguage === 'it',
      },
      {
        url: '/shop',
        icon: faShoppingCart,
        label: 'Corsi ECM',
        condition: () => {
          return (
            (!isLoggedIn && currentLanguage == 'it') ||
            (canAccessFeature('SHOP', currentLanguage, staticData, userDataFmt) && canSeeShop(subscription, userDataFmt, staticData, currentLanguage))
          );
        },
      },
    ],
    [isAso, country_code, isGamingEnabled, staticData, userDataFmt, currentLanguage, companyName, isCompanyEnabled]
  );

  useEffect(() => {
    setPagePath(window.location.pathname);
  }, []);

  useEffect(() => {
    if (!userDataFmt) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    setIsAso(myCategory == 'aso');
  }, [userDataFmt]);

  useEffect(() => {
    if (!userCompanyData) return;
    const companyId = userCompanyData?.id;
    const companyName = userCompanyData?.name;
    const isCompanyEnabled = !!userCompanyData?.enabled;

    setCompanyId(companyId);
    setCompanyName(companyName);
    setIsCompanyEnabled(isCompanyEnabled);
  }, [userCompanyData]);

  return (
    <>
      <Navbar className={'tootor-app-bar'} bg={bg} variant={variant as 'dark' | 'light'} sticky={'top'}>
        <Container fluid>
          <Navbar.Brand>
            {isLoggedIn && !isNative && (
              <Dropdown className={'d-inline-block d-md-none mr-2'}>
                <Dropdown.Toggle as={MenuButtonToggle} />
                <Dropdown.Menu>
                  {buttons.map((button, i) => {
                    const element = (
                      <TopbarDropdownNavItem
                        key={i.toString()}
                        pagePath={pagePath}
                        url={button.url}
                        icon={button.icon}
                        label={button.label}
                        onClick={button.onClick}
                        button={button}
                      />
                    );
                    if (!button.condition || button.condition()) return element;
                    return null;
                  })}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {!inHeader && <Logo className={'logo-tootor d-inline d-md-none'} variant={'reduced'} clickable />}
            {!inHeader && <Logo className={'logo-tootor d-none d-md-inline'} clickable />}
            {!isLoggedIn && currentLanguage === 'it' && (
              <div className={'d-inline-flex align-items-center ml-4'}>
                <div className={'d-none d-md-block'}>
                  {/* User is not already logged in, so we need to show the public route to the shop */}
                  {(!isLoggedIn && currentLanguage == 'it') ||
                  (canAccessFeature('SHOP', currentLanguage, staticData, userDataFmt) && canSeeShop(subscription, userDataFmt, staticData, currentLanguage)) ? (
                    <TopbarButton
                      pagePath={pagePath}
                      buttonDefinition={{
                        url: '/shop',
                        icon: faShoppingCart,
                        label: 'Acquista ECM',
                        condition: () => currentLanguage === 'it',
                      }}
                    />
                  ) : null}
                </div>
              </div>
            )}
            {isLoggedIn && (
              <div className={'d-inline-flex align-items-center ml-4'}>
                <div className={'d-none d-md-block d-flex align-items-center'}>
                  {buttons
                    .map((button, i) => {
                      const element = <TopbarButton pagePath={pagePath} key={i.toString()} buttonDefinition={button} />;
                      if (!button.condition || button.condition()) return element;
                      return null;
                    })
                    .filter(Boolean)}
                </div>
              </div>
            )}
          </Navbar.Brand>
          <Nav className={'ml-auto d-flex align-items-center'}>
            {userDataFmt?.profile?.formData?.categoriaProfessionale !== 'aso' && <LanguageSelector />}
            {!isLoggedIn && showLoginButtonIfNeeded && (
              <Button variant={'warning'} className={'tootor-palette-warning ml-2'} onClick={() => setIsLoginModalOpen(true)} size={'sm'}>
                {t('labels.accedi')}
              </Button>
            )}
            {isLoggedIn && (
              <>
                {!subscription && !isNative && (
                  <Button variant={'primary'} as={Link} to={'/plans'} className={'mr-2 ml-2'} size={'sm'}>
                    {t('labels.abbonati')}
                  </Button>
                )}
                {(!!subscription || !isMobile || isNative) && window.location.pathname !== '/notifications' ? (
                  <>
                    <IconButton
                      icon={faSearch}
                      to={'/search'}
                      className={'tootor-app-bar-icon-button ' + (isGamingEnabled ? '' : 'mr-2 ') + (!!subscription ? 'ml-2 ' : '')}
                    />
                    {/* {!isNative && <TopbarButton pagePath={pagePath} buttonDefinition={{ url: '/search', icon: faSearch, label: '' }} margin={false} />} */}
                    <Dropdown
                      className={'mr-2'}
                      onToggle={(isOpen) => {
                        if (isOpen) return;
                        cleanNotifications();
                      }}
                    >
                      <Dropdown.Toggle as={NotificationButtonToggle}>{notificationBadge}</Dropdown.Toggle>
                      <Dropdown.Menu align={'right'} style={{ minWidth: '300px', maxHeight: '500px', overflow: 'auto' }}>
                        <NotificationsList isDropdown={true} />
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                ) : (
                  <>
                    <IconButton icon={faSearch} to={'/search'} className={'tootor-app-bar-icon-button ' + (isGamingEnabled ? '' : 'mr-2')} />
                  </>
                )}

                <ProfileCircle />
              </>
            )}
          </Nav>
        </Container>
      </Navbar>

      <LoginModal isModalOpen={isLoginModalOpen} setIsModalOpen={setIsLoginModalOpen} />
    </>
  );
};

export { TopbarComponent as Topbar };

const MenuButtonToggle = forwardRef(({ onClick }: any, _) => (
  <Button
    variant={'outline-light'}
    className={'no-border'}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faBars} />
  </Button>
));

const NotificationButtonToggle = forwardRef(({ onClick, children: notificationBadge }: any, _) => (
  <IconButton
    className={'tootor-app-bar-icon-button'}
    icon={faBell}
    badge={<Badge variant={'danger'}>{!!notificationBadge && notificationBadge}</Badge>}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  />
));
